import React from 'react';
import './DropdownComponent.scss';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Zoom,
} from '@mui/material';
import DropdownProps from '../../@types/Dropdown.type';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { UnknownAction } from '@reduxjs/toolkit';

export const SELECT_STATION_FIRST_MESSAGE =
  'Please select a station first. Contact ITS Service Desk if not working as expected.';
export const STATION_LOADING_MESSAGE =
  'Station Data is loading. Contact ITS Service Desk if not working as expected.';
export const PACKET_REQUEST_LOADING_MESSAGE =
  'Packet Request is on the way. Please wait for the request to complete. Contact ITS Service Desk if not working as expected.';

const DropdownComponent: React.FC<DropdownProps> = ({
  labelId,
  label,
  id,
  menuItems,
  disabled,
  handleChange,
  handlePrinterCodeChange,
  error,
  defaultValue,
  value,
}) => {
  const dispatch = useAppDispatch();

  const { packetReqLoading } = useAppSelector((state) => state.appStateReducer);

  const items = menuItems?.map((item) => (
    <MenuItem key={item} value={item}>
      <span className="dropdown-item" data-testid="dropdown-item">
        {item}
      </span>
    </MenuItem>
  ));

  const tooltipProps = {
    arrow: true,
    TransitionComponent: Zoom,
    'data-testid': `dropdown-tooltip-${id}`,
  };

  // Read-only dropdown shown while data is loading or a prerequisite is missing.
  const disabledDropdown = (
    <FormControl fullWidth disabled error={error}>
      <InputLabel className="dropdown-label" id={labelId}>
        {label}
      </InputLabel>
      <Select
        labelId={labelId}
        id={id}
        value={value}
        defaultValue={defaultValue ?? ''}
        data-testid={`dropdown-${id}`}
      >
        {items}
      </Select>
    </FormControl>
  );

  // When there is a packet request loading
  if (packetReqLoading) {
    return (
      <Tooltip title={PACKET_REQUEST_LOADING_MESSAGE} {...tooltipProps}>
        {disabledDropdown}
      </Tooltip>
    );
  }

  // The printer dropdown is disabled until a station is selected; the station dropdown is
  // disabled while its data is loading (or the backend failed).
  if (disabled) {
    const title =
      label === 'City to print to'
        ? STATION_LOADING_MESSAGE
        : SELECT_STATION_FIRST_MESSAGE;
    return (
      <Tooltip title={title} {...tooltipProps}>
        <span style={{ width: '100%' }}>{disabledDropdown}</span>
      </Tooltip>
    );
  }

  const isCitySelect = Boolean(labelId?.includes('city-select-label'));
  let onSelectChange: ((selected: string) => void) | undefined;
  if (handleChange) {
    onSelectChange = (selected) =>
      dispatch(handleChange(selected) as UnknownAction);
  } else if (handlePrinterCodeChange && !isCitySelect) {
    onSelectChange = handlePrinterCodeChange;
  }

  if (!onSelectChange && !isCitySelect) {
    return (
      <p>You have to pass handleChange or handlePrinterCodeChange props</p>
    );
  }

  return (
    <FormControl fullWidth error={error}>
      <InputLabel className="dropdown-label" id={labelId}>
        {label}
      </InputLabel>
      <Select
        labelId={labelId}
        id={id}
        value={value}
        label={label}
        data-testid={`dropdown-${id}`}
        onChange={(e) => onSelectChange?.(e?.target?.value)}
        required
        // Default value is passed for the Flight Date dropdown
        defaultValue={defaultValue ?? ''}
      >
        {items}
      </Select>
      {error && (
        <FormHelperText id="dropdown-error-helper-text">
          <b>Please enter a value</b>
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default DropdownComponent;
